import React, { useState } from 'react';
import { backendURL } from 'config';

/**
 * The enquiry form on a brand's contact section.
 *
 * Plain elements and inline styles, like everything else the public renderer
 * draws: this runs on the brand's own domain outside our theme, and has to
 * take the colours of the section it sits in.
 *
 * Where the message goes is decided server-side from the section. A form that
 * carried its own recipient would be an open relay on someone else's domain.
 *
 * Success replaces the form rather than showing a message beside it. Leaving
 * a filled-in form on screen after sending invites a second send, and two
 * identical enquiries is a worse outcome than a slightly abrupt confirmation.
 */
export default function ContactForm({ section, textColor, brandRef, pageSlug }) {
  const [values, setValues] = useState({ name: '', email: '', message: '' });
  const [state, setState] = useState({ phase: 'idle', error: '' });

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const fieldStyle = {
    width: '100%',
    padding: '11px 13px',
    borderRadius: 10,
    border: '1px solid rgba(127,127,127,0.35)',
    background: 'rgba(127,127,127,0.06)',
    color: textColor,
    fontSize: 15,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const submit = (e) => {
    e.preventDefault();
    setState({ phase: 'sending', error: '' });
    // Plain fetch rather than the dashboard's API client: this component
    // renders on the public site, and pulling the authenticated axios client
    // into that bundle brings interceptors, token handling and refresh logic
    // that a visitor's contact form has no business carrying.
    fetch(`${backendURL}/public/site/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(brandRef ? { brand: brandRef } : {}),
        section_id: section?._id,
        page_slug: pageSlug,
        name: values.name,
        email: values.email,
        message: values.message,
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Could not send that.');
        setState({ phase: 'sent', error: '' });
      })
      .catch((err) => setState({
        phase: 'idle',
        error: err?.message || 'Could not send that. Please try again.',
      }));
  };

  if (state.phase === 'sent') {
    return (
      <div
        style={{
          maxWidth: 520,
          margin: '0 auto',
          padding: '22px',
          borderRadius: 12,
          border: '1px solid rgba(127,127,127,0.25)',
          background: 'rgba(127,127,127,0.06)',
          color: textColor,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Thanks — that’s on its way.</div>
        <div style={{ opacity: 0.75, fontSize: 14 }}>
          We’ll reply to {values.email}.
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      style={{ maxWidth: 520, margin: '0 auto', textAlign: 'left' }}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <input
          required
          placeholder='Your name'
          value={values.name}
          onChange={set('name')}
          style={fieldStyle}
        />
        <input
          required
          type='email'
          placeholder='Your email'
          value={values.email}
          onChange={set('email')}
          style={fieldStyle}
        />
        <textarea
          required
          rows={5}
          placeholder='How can we help?'
          value={values.message}
          onChange={set('message')}
          style={{ ...fieldStyle, resize: 'vertical' }}
        />

        {state.error && (
          <div style={{ color: '#dc2626', fontSize: 14 }}>{state.error}</div>
        )}

        <button
          type='submit'
          disabled={state.phase === 'sending'}
          style={{
            padding: '12px 22px',
            borderRadius: 10,
            border: 'none',
            background: '#4F46E5',
            color: '#fff',
            fontWeight: 600,
            fontSize: 15,
            cursor: state.phase === 'sending' ? 'default' : 'pointer',
            opacity: state.phase === 'sending' ? 0.7 : 1,
            justifySelf: 'start',
          }}
        >
          {state.phase === 'sending' ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
}
